import React from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { ResponsiveImage, Link } from "@quintype/components";
import { ReportSlider } from "./report-slider";
import { KeyIcon } from "./icons";
import { SectionLink, ColumnBlock, EventCard } from "./components";
import { getLinkTarget } from "../../../arrow/utils/utils";
import "./ceo-forum.m.css";
const getForumType = (breadcrumbText) => {
  const plainText = (breadcrumbText || "").replace(/<[^>]*>/g, "").trim();
  const match = plainText.match(/^(?:IMA\s+)?(.*?)(?:\s+FORUM)?$/i);
  return match ? match[1].trim().toUpperCase() : "CEO";
};

export const CeoForumPage = ({ data }) => {
  const { forumPageData, sliderData } = data || {};

  const fallbackImage = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "events_fallback"], "")
  );

  const breadcrumb = forumPageData?.breadcrumb || "";
  const headline = forumPageData?.headline || "";
  const testimonial = forumPageData?.testimonial || {};
  const introText = forumPageData?.introText || "";
  const executiveExperiences = forumPageData?.executiveExperiences || {};
  const eyebrow = executiveExperiences?.eyebrow || "";
  const expHeadline = executiveExperiences?.headline || "";
  const apiColumns = forumPageData?.columns || [];
  const columns = apiColumns.slice(0, 2).map((col) => ({ ...col, variant: "dark" }));
  const bottomColumns = apiColumns.slice(2, 4).map((col) => ({ ...col, variant: "tint" }));
  const footnote = forumPageData?.footnote || "";
  const events = forumPageData?.eventsCalendar || [];
  const eventsCollectionData = forumPageData?.eventsHeroImage || {};
  const coverImageS3Key = eventsCollectionData?.["cover-image-s3-key"] || "";
  const coverImageMetadata = eventsCollectionData?.["cover-image-metadata"] || {};

  const tailoredResearch = forumPageData?.tailoredResearch || {};
  const resEyebrow = tailoredResearch?.eyebrow || "";
  const resHeadline = tailoredResearch?.headline || "";
  const resDescription = tailoredResearch?.description || "";

  const forumType = getForumType(breadcrumb);
  const calHeadline = `${forumType} events calendar`;
  const resFootnote = `*${forumType} exclusive`;
  const joinGroupName = forumPageData?.joinGroupName;
  const becomeMemberHeader = forumPageData?.becomeMemberHeader;
  const becomeMemberDesc = forumPageData?.becomeMemberDesc;

  return (
    <div className="page-container" styleName="ceo-forum-page">


      <section styleName="hero-section">
        <div styleName="hero-content">
          <div styleName="hero-top">
            <div styleName="hero-breadcrumb" dangerouslySetInnerHTML={{ __html: breadcrumb }} />
            <h1 styleName="hero-headline" dangerouslySetInnerHTML={{ __html: headline }} />
          </div>
          <div styleName="hero-testimonials desktop-testimonial">
            <p styleName="testimonial-quote">&ldquo;<span dangerouslySetInnerHTML={{ __html: testimonial.quote }} />&rdquo;</p>
            <div styleName="testimonial-author">
              <div styleName="author-name" dangerouslySetInnerHTML={{ __html: testimonial.author }} />
              <div styleName="author-separator" />
              <div styleName="author-designation" dangerouslySetInnerHTML={{ __html: testimonial.designation }} />
            </div>
          </div>
          <div styleName="hero-testimonials mobile-testimonial">
            <p styleName="testimonial-quote">&ldquo;<span dangerouslySetInnerHTML={{ __html: testimonial.quote }} />&rdquo;</p>
            <div styleName="testimonial-author">
              <div styleName="author-name" dangerouslySetInnerHTML={{ __html: testimonial.author }} />
              <div styleName="author-designation" dangerouslySetInnerHTML={{ __html: testimonial.designation }} />
            </div>
          </div>
        </div>
      </section>

      {introText && (
        <section styleName="intro-section">
          <div styleName="centered">
            <div styleName="content-container">
              <div styleName="intro-text" dangerouslySetInnerHTML={{ __html: introText }} />
            </div>
          </div>
        </section>
      )}

      {<section styleName="research-section">
        <div styleName="centered">
          <div styleName="content-container">
            {(resEyebrow || resHeadline) && (
              <div styleName="section-header">
                {resEyebrow && <div styleName="section-eyebrow" dangerouslySetInnerHTML={{ __html: resEyebrow }} />}
                {resHeadline && <h2 styleName="section-headline" dangerouslySetInnerHTML={{ __html: resHeadline }} />}
              </div>
            )}
            {resDescription && <div styleName="research-description" dangerouslySetInnerHTML={{ __html: resDescription }} />}
            <ReportSlider reports={sliderData || []} />
            <div styleName="slider-footer">
              <SectionLink text="Explore research hub" href="/collection/research-hub" />
              {sliderData && sliderData.length > 0 && (
                <div styleName="slider-footnote">
                  <p styleName="footnote-text">{resFootnote}</p>
                  <KeyIcon clipId="clip_footer" />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>}

      {(eyebrow || expHeadline || columns.length > 0 || bottomColumns.length > 0) && (
        <section styleName="experiences-section">
          <div styleName="centered">
            <div styleName="content-container">
              {(eyebrow || expHeadline) && (
                <div styleName="section-header">
                  {eyebrow && <div styleName="section-eyebrow" dangerouslySetInnerHTML={{ __html: eyebrow }} />}
                  {expHeadline && <h2 styleName="section-headline" dangerouslySetInnerHTML={{ __html: expHeadline }} />}
                </div>
              )}
              <div styleName="columns-row">
                {columns.map(({ title, variant, items }, idx) => (
                  title && items?.length > 0 && <ColumnBlock key={idx} title={title} items={items} variant={variant} hasTitle />
                ))}
              </div>
              <div styleName="columns-row">
                {bottomColumns.map(({ title, variant, items }, idx) => (
                  title && items?.length > 0 && <ColumnBlock key={idx} title={title} items={items} variant={variant} hasTitle={false} />
                ))}
              </div>
              {footnote && <div styleName="section-footnote" dangerouslySetInnerHTML={{ __html: footnote }} />}
            </div>
          </div>
        </section>
      )}

      {<section styleName="calendar-section">
        <div styleName="calendar-background">
          <img
            src={fallbackImage}
            alt="Events Calendar Background"
            styleName="calendar-bg-image"
          />
        </div>
        <div styleName="calendar-content">
          <div styleName="centered">
            <div styleName="content-container">
              <div styleName="calendar-header">
                <h2 styleName="section-headline section-headline-white">{calHeadline}</h2>
              </div>
              <div styleName="events-row">
                {events.map(({ title, duration, location }, idx) => (
                  <EventCard key={idx} title={title} duration={duration} location={location} />
                ))}
              </div>
              <div styleName="calendar-link">
                <SectionLink text="Explore conferences" href="/events/conferences" />
              </div>
            </div>
          </div>
        </div>
      </section>}

      <section styleName="become-member-section">
        <h2 styleName="member-headline">{becomeMemberHeader}</h2>
        <p styleName="member-description">{becomeMemberDesc}</p>
        <p styleName="know-more">To know more see <Link href="/faqs" styleName="know-more-link" target={getLinkTarget("/faqs")} rel="noopener">FAQs</Link></p>
        <div styleName="member-cta">
          <Link href="/become-a-member" styleName="member-button">Get started</Link>
        </div>
      </section>



      <div styleName="bottom-nav">
        <div styleName="bottom-nav-content">
          <Link href="/become-a-member" styleName="nav-button">Become a member</Link>
        </div>
      </div>
    </div >
  );
};

export default CeoForumPage;
